import {
  HEADER_FILL,
  NA_FILL,
  TITLE_FONT,
  VALUE_FILL,
  hideRowsThrough,
} from "./detailSheetCommon.js";
import {
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  borderFromSpecs,
  internalSheetLink,
  parseBorderSpec,
  safeText,
  setCell,
  setGrey,
} from "./principle6SheetHelpers.js";

const ACTION_TAKEN_ON_ISSUE_SHEET = "Action taken on issue";
const PUBLIC_POLICY_ADVOCATES_SHEET = "Public policy advocates";
const DETAIL_CELL = "D8";
const PUBLIC_POLICY_CELL = "D7";

const PRINCIPLE_TITLE =
  "PRINCIPLE 7 Businesses, when engaging in influencing public and regulatory policy, should do so in a manner that is responsible and transparent";

const SECTION_FONT = { name: "Calibri", size: 12, bold: true };
const AFFILIATION_ROW_COUNT = 10;

function setColumnWidths(ws) {
  const widths = {
    A: 0.7265625,
    B: 0.7265625,
    C: 11.7265625,
    D: 6.26953125,
    E: 55.7265625,
    F: 63.54296875,
    G: 35.453125,
    H: 9.1796875,
    I: 9.1796875,
    J: 11.81640625,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  ["A", "B", "H", "I"].forEach((col) => {
    ws.getColumn(col).hidden = true;
  });
  for (let col = 11; col <= 16384; col += 1) {
    ws.getColumn(col).hidden = true;
  }
}

function setupSheetView(workbook, ws) {
  ws.views = [
    {
      showGridLines: false,
      state: "frozen",
      xSplit: 0,
      ySplit: 8,
      topLeftCell: "A9",
      zoomScale: 70,
      zoomScaleNormal: 70,
    },
  ];
  if (!workbook.views?.length) {
    workbook.views = [{ showGridLines: false }];
  } else {
    workbook.views[0].showGridLines = false;
  }
}

function writeBand(ws, row, endCol, text, specs, options = {}) {
  for (let col = COL_D; col <= endCol; col += 1) {
    setCell(ws, row, col, col === COL_D ? text : "", {
      font: options.font,
      bold: options.bold,
      align: options.align || "left",
      fill: options.fill,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  if (endCol > COL_D) {
    try {
      ws.mergeCells(row, COL_D, row, endCol);
    } catch {
      /* already merged */
    }
    const startBorder = parseBorderSpec(specs[0]);
    const endBorder = parseBorderSpec(specs[specs.length - 1]);
    ws.getCell(row, COL_D).border = {
      top: startBorder.top || endBorder.top,
      left: startBorder.left,
      bottom: startBorder.bottom || endBorder.bottom,
      right: endBorder.right || startBorder.right,
    };
    if (options.fill) ws.getCell(row, COL_D).fill = options.fill;
    if (options.font) ws.getCell(row, COL_D).font = options.font;
  }
  if (options.height != null) ws.getRow(row).height = options.height;
}

function getAffiliationRows(list) {
  const rows = Array.isArray(list) ? list : [];
  return Array.from({ length: AFFILIATION_ROW_COUNT }, (_, index) => rows[index] || {});
}

export function buildPrinciple7Sheet(workbook, sectionC) {
  const ws = workbook.addWorksheet("PRINCIPLE 7");
  setColumnWidths(ws);
  setupSheetView(workbook, ws);
  hideRowsThrough(ws, 5);

  const principle = sectionC?.principle7 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const affiliations = getAffiliationRows(essential?.listOf10TradeandIndustry);

  // R8 title
  writeBand(ws, 8, COL_G, PRINCIPLE_TITLE, ["mmtt", "mttt", "mttt", "mttm"], {
    font: TITLE_FONT,
    fill: HEADER_FILL,
    align: "center",
    height: 38.25,
  });

  // R9 Essential Indicators
  writeBand(ws, 9, COL_G, "Essential Indicators", ["tmtt", "tttt", "tttt", "tt-m"], {
    font: SECTION_FONT,
    fill: NA_FILL,
    align: "center",
    height: 20.15,
  });

  // R10 Q1a
  writeBand(
    ws,
    10,
    COL_F,
    "1. a. Number of affiliations with trade and industry chambers/ associations.",
    ["tmt-", "t-t-", "t-tt"],
    { height: 20.15 }
  );
  setCell(ws, 10, COL_G, safeText(essential?.numberOfAffiliationsWithTrade, ""), {
    align: "center",
    fill: VALUE_FILL,
    bordered: false,
    border: parseBorderSpec("tttm"),
  });

  // R11 Q1b intro
  writeBand(
    ws,
    11,
    COL_F,
    "b. List the top 10 trade and industry chambers/ associations (determined based on the total members of such body) the entity is a member of/ affiliated to",
    ["tmt-", "t-t-", "t-tt"],
    { bold: true, height: 32.25 }
  );
  setGrey(ws, 11, COL_G, parseBorderSpec("-t-m"));

  // R12 headers
  setCell(ws, 12, COL_D, "Sr. No.", {
    align: "center",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  setCell(ws, 12, COL_E, "Name of the trade and industry chambers/ associations", {
    align: "center",
    bordered: false,
    border: parseBorderSpec("tttt"),
  });
  setCell(
    ws,
    12,
    COL_F,
    "Reach of trade and industry chambers/ associations (State/National/International)",
    {
      align: "center",
      bordered: false,
      border: parseBorderSpec("ttt-"),
    }
  );
  setGrey(ws, 12, COL_G, parseBorderSpec("-t-m"));
  ws.getRow(12).height = 29;

  // R13–22 affiliation rows (always 10)
  affiliations.forEach((item, idx) => {
    const row = 13 + idx;
    const hasData =
      Boolean(String(item?.name || "").trim()) || Boolean(String(item?.reach || "").trim());
    const valueFill = hasData ? VALUE_FILL : NA_FILL;
    const isLast = idx === AFFILIATION_ROW_COUNT - 1;

    setCell(ws, row, COL_D, String(idx + 1), {
      align: "center",
      bordered: false,
      border: parseBorderSpec("tmtt"),
    });
    setCell(ws, row, COL_E, hasData ? safeText(item?.name, "") : "", {
      align: "left",
      fill: valueFill,
      bordered: false,
      border: parseBorderSpec("tttt"),
    });
    setCell(ws, row, COL_F, hasData ? safeText(item?.reach, "") : "", {
      align: "left",
      fill: valueFill,
      bordered: false,
      border: parseBorderSpec("ttt-"),
    });
    setGrey(ws, row, COL_G, parseBorderSpec(isLast ? "-ttm" : "-t-m"));
  });

  // R23 Q2 + Add details
  writeBand(
    ws,
    23,
    COL_F,
    "2. Provide details of corrective action taken or underway on any issues related to anti- competitive conduct by the entity, based on adverse orders from regulatory authorities.",
    ["tmt-", "t-t-", "t-tt"],
    { height: 30 }
  );
  setCell(ws, 23, COL_G, "Add details", {
    align: "center",
    link: internalSheetLink(ACTION_TAKEN_ON_ISSUE_SHEET, DETAIL_CELL),
    bordered: false,
    border: parseBorderSpec("-ttm"),
  });

  // R24 Leadership
  writeBand(ws, 24, COL_G, "Leadership Indicators", ["tmtt", "tttt", "tttt", "tttm"], {
    font: SECTION_FONT,
    fill: NA_FILL,
    align: "center",
    height: 15.5,
  });

  // R25 public policy + Add details
  writeBand(
    ws,
    25,
    COL_F,
    "1. Details of public policy positions advocated by the entity:",
    ["tmtt", "tttt", "ttt-"],
    { height: 30 }
  );
  setCell(ws, 25, COL_G, "Add details", {
    align: "center",
    link: internalSheetLink(PUBLIC_POLICY_ADVOCATES_SHEET, PUBLIC_POLICY_CELL),
    bordered: false,
    border: parseBorderSpec("tttm"),
  });

  // R26 Notes
  writeBand(ws, 26, COL_F, "Notes", ["tmm-", "t-m-", "t-mt"], { height: 30 });
  setCell(ws, 26, COL_G, safeText(principle?.notes || leadership?.notes || essential?.notes, ""), {
    align: "left",
    fill: VALUE_FILL,
    bordered: false,
    border: parseBorderSpec("ttmm"),
  });

  return ws;
}
